import React, { useCallback, useEffect, useMemo } from 'react';
import Input from '../core/Input';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Dropdown, OptionsType } from '../core/Dropdown';
import IconPlus from '@allbin/icons/icons/IconPlus';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '../../helpers/classnames';
import Permissions from '../auth/Permissions';
import Button from '../core/Button';
import { IconTrash } from '@/icons';
import {
  contractorKindMessages,
  contractorKinds,
  defaultContractorKind,
} from '@/helpers/contractor_kind';

const optionalEmail = (
  invalidEmail: string,
): z.ZodUnion<[z.ZodLiteral<''>, z.ZodString]> =>
  z.union([z.literal(''), z.string().email(invalidEmail)]);

const optionalPhone = (
  invalidPhone: string,
): z.ZodUnion<[z.ZodLiteral<''>, z.ZodString]> =>
  z.union([
    z.literal(''),
    z.string().regex(/^\+[1-9][0-9]{1,14}$/, invalidPhone),
  ]);

interface Props {
  contractor?: Contractor;
  filtersets: FilterSet[];
  isLoading: boolean;
  onDirty: (dirty: boolean) => void;
  onRemove?: () => void;
  onSave: (c: ContractorRequest) => void;
}

const messages = defineMessages({
  empty_field: {
    id: 'contractor_schema.empty_field',
    defaultMessage: 'Field cannot be empty',
  },
  invalid_email: {
    id: 'contractor_schema.invalid_email',
    defaultMessage: 'Invalid email address',
  },
  invalid_phone: {
    id: 'contractor_schema.invalid_phone',
    defaultMessage: 'Invalid phone number',
  },
  email_required_for_road_authority: {
    id: 'contractor_schema.email_required_for_road_authority',
    defaultMessage:
      'Required for road authorities: work orders reach them by email only',
  },
  no_filterset: {
    id: 'contractor_form.no_filterset',
    defaultMessage: 'No filterset',
  },
  organization_name: {
    id: 'contractor_form.organization_name',
    defaultMessage: 'Organization name',
  },
  kind: {
    id: 'contractor_form.kind',
    defaultMessage: 'Type',
  },
  contact_person: {
    id: 'contractor_form.contact_person',
    defaultMessage: 'Contact person',
  },
  phone_number: {
    id: 'contractor_form.phone_number',
    defaultMessage: 'Phone number',
  },
  email: {
    id: 'contractor_form.email',
    defaultMessage: 'Email',
  },
  notes: {
    id: 'contractor_form.notes',
    defaultMessage: 'Internal note',
  },
  filterset: {
    id: 'contractor_form.filterset',
    defaultMessage: 'Filterset',
  },
});

export const ContractorForm: React.FC<Props> = ({
  contractor,
  filtersets,
  isLoading,
  onDirty,
  onRemove,
  onSave,
}) => {
  const intl = useIntl();

  const schema = useMemo(
    () =>
      z
        .object({
          name: z
            .string()
            .trim()
            .min(1, intl.formatMessage(messages.empty_field)),
          kind: z.enum(contractorKinds),
          email: optionalEmail(intl.formatMessage(messages.invalid_email)),
          address: z.string(),
          phone: optionalPhone(intl.formatMessage(messages.invalid_phone)),
          contact_person: z.string(),
          filterset_id: z.string(),
          notes: z.string(),
        })
        // A road authority has no HållKoll account; the API reaches it only by
        // mail, and skips one without an address silently (HK-1115).
        .superRefine((data, ctx) => {
          if (data.kind === 'road_authority' && data.email === '') {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['email'],
              message: intl.formatMessage(
                messages.email_required_for_road_authority,
              ),
            });
          }
        }),
    [intl],
  );
  type FormData = z.infer<typeof schema>;

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting, isValid, isDirty, errors },
  } = useForm<FormData>({
    defaultValues: {
      name: contractor?.name || '',
      kind: contractor?.kind ?? defaultContractorKind,
      email: contractor?.email || '',
      phone: contractor?.phone || '',
      address: contractor?.address || '',
      contact_person: contractor?.contact_person || '',
      filterset_id: contractor?.filterset_id || '',
      notes: contractor?.notes || '',
    },
    resolver: zodResolver(schema),
    reValidateMode: 'onChange',
    mode: 'onChange',
  });

  const nameValue = watch('name');
  const kindValue = watch('kind');
  const emailRequired = kindValue === 'road_authority';
  // Said up front, not only after the field is touched, so a stored road
  // authority without an address shows on open why it cannot be saved.
  const emailError =
    errors.email?.message ??
    (emailRequired && watch('email') === ''
      ? intl.formatMessage(messages.email_required_for_road_authority)
      : undefined);
  const canSave =
    nameValue.trim().length > 0 &&
    isValid &&
    isDirty &&
    !isSubmitting &&
    !isLoading;

  // register() also returns validation attributes such as `min`/`max`, which
  // clash with the per-type prop union on the core Input, so only the wiring
  // is forwarded.
  const field = useCallback(
    (name: keyof FormData) => {
      const { ref, onChange, onBlur, name: fieldName } = register(name);
      return { ref, onChange, onBlur, name: fieldName };
    },
    [register],
  );

  const filtersetOptions = useMemo(() => {
    const options = filtersets.map<OptionsType>((filterset) => ({
      id: filterset.id,
      label: filterset.name,
      value: filterset.id,
    }));
    options.unshift({
      label: intl.formatMessage(messages.no_filterset),
      value: '',
      id: '',
    });
    return options;
  }, [filtersets, intl]);

  const kindOptions = useMemo(
    () =>
      contractorKinds.map<OptionsType>((kind) => ({
        id: kind,
        label: intl.formatMessage(contractorKindMessages[kind]),
        value: kind,
      })),
    [intl],
  );

  const onSubmit: SubmitHandler<FormData> = useCallback(
    (data) => {
      const request: ContractorRequest = {
        name: data.name,
        kind: data.kind,
        email: data.email || undefined,
        phone: data.phone || undefined,
        address: data.address || undefined,
        contact_person: data.contact_person || undefined,
        filterset_id: data.filterset_id || undefined,
        notes: data.notes || undefined,
      };
      onSave(request);
      reset(data);
    },
    [onSave, reset],
  );

  useEffect(() => {
    reset();
  }, [reset]);

  useEffect(() => {
    onDirty(isDirty);
  }, [isDirty, onDirty]);

  return (
    <>
      <form
        className="flex w-full flex-col gap-y-8"
        noValidate
        //eslint-disable-next-line
        onSubmit={handleSubmit(onSubmit)}
      >
        <Input
          {...field('name')}
          id="organization_name"
          label={intl.formatMessage(messages.organization_name)}
          placeholder=""
          disabled={isLoading}
          type="text"
          required
          error={errors.name?.message}
          helperTextReserveSpace
        />
        <Controller
          name="kind"
          control={control}
          // The email rule depends on the kind; re-validate it on change.
          rules={{ deps: ['email'] }}
          render={({ field }) => (
            <Dropdown
              id="contractor-kind"
              options={kindOptions}
              value={kindOptions.find((o) => o.id === field.value)}
              onValueChange={(option) => {
                field.onChange(option.id);
              }}
              label={intl.formatMessage(messages.kind)}
              disabled={isLoading}
            />
          )}
        />
        <Input
          {...field('contact_person')}
          id="contact_person"
          label={intl.formatMessage(messages.contact_person)}
          placeholder=""
          disabled={isLoading}
          type="text"
          error={errors.contact_person?.message}
          helperTextReserveSpace
        />
        <Input
          {...field('phone')}
          id="phone_number"
          label={intl.formatMessage(messages.phone_number)}
          disabled={isLoading}
          type="tel"
          placeholder="+46123456789"
          error={errors.phone?.message}
          helperTextReserveSpace
        />
        <Input
          {...field('email')}
          id="email"
          label={intl.formatMessage(messages.email)}
          placeholder="example@domain.com"
          disabled={isLoading}
          type="text"
          required={emailRequired}
          error={emailError}
          helperTextReserveSpace
        />
        <Input
          {...field('notes')}
          id="notes"
          label={intl.formatMessage(messages.notes)}
          placeholder=""
          disabled={isLoading}
          type="text"
          helperTextReserveSpace
        />
        <Controller
          name="filterset_id"
          control={control}
          render={({ field }) => (
            <Dropdown
              id="contractor-filterset"
              options={filtersetOptions}
              value={filtersetOptions.find((o) => o.id === field.value)}
              onValueChange={(option) => {
                field.onChange(option.id);
              }}
              label={intl.formatMessage(messages.filterset)}
              placeholder={intl.formatMessage(messages.no_filterset)}
              disabled={isLoading}
            />
          )}
        />
        {isLoading && (
          <div
            className="absolute inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-white border-e-white/0 align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
            role="status"
          />
        )}
        <div className={cn('flex w-full p-2')}>
          {onRemove && (
            <Permissions has="contractors:delete">
              <Button
                variant="ghost"
                color="red"
                startIcon={<IconTrash />}
                onClick={onRemove}
                disabled={isLoading}
              >
                <FormattedMessage
                  id="edit_contractor_dialog.remove"
                  defaultMessage="Remove"
                />
              </Button>
            </Permissions>
          )}
          <Button
            type="submit"
            disabled={!canSave}
            variant="filled"
            startIcon={<IconPlus />}
            className="ml-auto"
          >
            {contractor ? (
              <FormattedMessage
                id="edit_contractor_dialog.save"
                defaultMessage="Save contractor"
              />
            ) : (
              <FormattedMessage
                id="edit_contractor_dialog.create_contractor"
                defaultMessage="Create contractor"
              />
            )}
          </Button>
        </div>
      </form>
    </>
  );
};
